import { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { LogIn, Shield, KeyRound, Mail, ArrowLeft, User, Lock } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function Login() {
  const { login, setUser } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("google");  // 'google' | 'password'
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submitPassword = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login-password", { username, password });
      setUser(data);
      toast.success(`Witaj, ${data.name || data.username}!`);
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Nieprawidłowy login lub hasło");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 px-4">
      <div
        data-testid="login-page"
        className="w-full max-w-md border border-zinc-900 bg-white p-10 shadow-[8px_8px_0_0_#18181b]"
      >
        <Link
          to="/welcome"
          data-testid="login-back-to-welcome"
          className="inline-flex items-center gap-2 mb-6 font-mono text-[10px] uppercase tracking-wider text-zinc-500 hover:text-brand"
        >
          <ArrowLeft className="w-3 h-3" /> Powrót
        </Link>
        <div className="mb-8">
          <img
            src="/assets/gopossible-header.png"
            alt="GoPossible · Powered by Ambitions"
            className="h-14 w-auto"
            onError={(e) => { e.currentTarget.src = "/assets/gopossible-logo.png"; }}
          />
        </div>

        <div className="font-label mb-2">// Logowanie</div>
        <h1 className="font-heading font-black text-3xl uppercase tracking-tight mb-3">
          Zaloguj się
        </h1>

        {/* Tab switcher */}
        <div className="grid grid-cols-2 mb-6 border border-zinc-900">
          <button
            data-testid="tab-google"
            onClick={() => setTab("google")}
            className={`py-2 px-3 font-mono text-xs uppercase tracking-wider transition-colors ${
              tab === "google" ? "bg-zinc-900 text-white" : "bg-white hover:bg-zinc-100"
            }`}
          >
            Google
          </button>
          <button
            data-testid="tab-password"
            onClick={() => setTab("password")}
            className={`py-2 px-3 font-mono text-xs uppercase tracking-wider transition-colors ${
              tab === "password" ? "bg-zinc-900 text-white" : "bg-white hover:bg-zinc-100"
            }`}
          >
            Login + hasło
          </button>
        </div>

        {tab === "google" && (
          <button
            onClick={login}
            data-testid="btn-google-login"
            className="w-full inline-flex items-center justify-center gap-3 bg-zinc-900 hover:bg-brand text-white px-6 py-4 font-bold uppercase tracking-wider transition-colors"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="currentColor" d="M21.6 12.2c0-.7-.1-1.4-.2-2H12v3.9h5.4c-.2 1.3-.9 2.4-2 3.1v2.6h3.2c1.9-1.7 3-4.3 3-7.6z"/>
              <path fill="currentColor" d="M12 22c2.7 0 5-.9 6.6-2.4l-3.2-2.6c-.9.6-2 1-3.4 1-2.6 0-4.8-1.8-5.6-4.1H3.1v2.6C4.7 19.8 8.1 22 12 22z"/>
              <path fill="currentColor" d="M6.4 13.9c-.2-.6-.3-1.2-.3-1.9s.1-1.3.3-1.9V7.5H3.1C2.4 8.9 2 10.4 2 12s.4 3.1 1.1 4.5l3.3-2.6z"/>
              <path fill="currentColor" d="M12 5.9c1.5 0 2.8.5 3.9 1.5l2.8-2.8C17 3.1 14.7 2 12 2 8.1 2 4.7 4.2 3.1 7.5l3.3 2.6C7.2 7.8 9.4 5.9 12 5.9z"/>
            </svg>
            Zaloguj przez Google
            <LogIn className="w-4 h-4 ml-1" />
          </button>
        )}

        {tab === "password" && (
          <form onSubmit={submitPassword} className="space-y-4" data-testid="password-login-form">
            <div>
              <label className="font-label block mb-1.5">Login</label>
              <div className="relative">
                <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  autoComplete="username"
                  data-testid="input-username"
                  className="w-full border border-zinc-300 pl-10 pr-3 py-3 focus:outline-none focus:border-zinc-900"
                  placeholder="np. testtest0gp"
                />
              </div>
            </div>
            <div>
              <label className="font-label block mb-1.5">Hasło</label>
              <div className="relative">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  data-testid="input-password"
                  className="w-full border border-zinc-300 pl-10 pr-3 py-3 focus:outline-none focus:border-zinc-900"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={busy}
              data-testid="btn-password-login"
              className="w-full bg-zinc-900 hover:bg-brand text-white px-6 py-3 font-bold uppercase tracking-wider transition-colors disabled:opacity-60"
            >
              {busy ? "Loguję..." : "Zaloguj się"}
            </button>
            <Link
              to="/register"
              data-testid="link-register"
              className="block text-center text-xs font-mono uppercase tracking-wider text-zinc-500 hover:text-brand pt-2"
            >
              nie masz konta? zarejestruj się →
            </Link>
          </form>
        )}

        <div className="mt-10 grid grid-cols-3 gap-4 text-[11px] text-zinc-500">
          <Feature icon={Shield} label="Sesja 7 dni" />
          <Feature icon={KeyRound} label="2 metody loginu" />
          <Feature icon={Mail} label="Approval przez admina" />
        </div>

        <div className="mt-8 pt-6 border-t border-zinc-200 text-[11px] font-mono text-zinc-400 text-center">
          GoPossible Sp. z o.o. · NIP 8513329264
        </div>
      </div>
    </div>
  );
}

function Feature({ icon: Icon, label }) {
  return (
    <div className="flex flex-col items-center gap-1 text-center">
      <Icon className="w-4 h-4 text-brand" />
      <span>{label}</span>
    </div>
  );
}
